/**
 * Reducer for the lexicons module (VE-24948 / U5).
 *
 * Duck convention, `createReducer` from Redux Toolkit — not `createSlice`, per
 * the repo's module pattern. All state is treated as immutable; every handler
 * returns a new object rather than mutating a draft, matching the surrounding
 * modules.
 */

import { createReducer } from '@reduxjs/toolkit';
import { keyBy } from 'lodash';

import {
  ADD_DRAFT_ENTRIES,
  ADD_LEXICON_ENTRIES,
  CANCEL_CSV_IMPORT,
  CLEAR_LEXICON_CONFLICT,
  CLEAR_MUTATION_NOTICE,
  LEXICON_MUTATION_SETTLED,
  CLOSE_LEXICON_DRAWER,
  CREATE_LEXICON,
  DELETE_LEXICON,
  FETCH_LEXICONS,
  FETCH_LEXICONS_FAILURE,
  FETCH_LEXICONS_SUCCESS,
  RELOAD_EDITING_LEXICON_SUCCESS,
  LEXICON_DELETED,
  LEXICON_MUTATION_AUDIT_INCOMPLETE,
  LEXICON_MUTATION_BLOCKED_BY_AUDIT,
  LEXICON_MUTATION_FAILURE,
  LEXICON_MUTATION_SUCCESS,
  LEXICONS_NOT_CONFIGURED,
  OPEN_LEXICON_DRAWER,
  PURGE_DELETED_DRAFT_ENTRIES,
  PURGE_DRAFT_ENTRY,
  REMOVE_DRAFT_ENTRY,
  RESTORE_DRAFT_ENTRY,
  UPDATE_DRAFT_ENTRY,
  REMOVE_LEXICON_ENTRIES,
  RENAME_LEXICON,
  SAVE_LEXICON_DRAFT,
  SET_CSV_IMPORT_RESULT,
  SET_DRAFT_NAME,
  SET_DRAFT_NAME_ERROR,
  SET_LEXICON_CONFLICT,
  SET_LEXICON_FLAGS,
  SET_PAGE,
  SET_SHOW_DELETED_LEXICONS,
  START_CSV_IMPORT,
} from '../actions';
import {
  DRAFT_ENTRY_ID_PREFIX,
  Lexicon,
  LexiconEntry,
  LexiconsState,
  deletedEntries,
  isDraftEntryId,
  liveEntries,
} from '../models';
import { defaultState, namespace } from '../store';
import { lexiconEntryDuplicateKey } from '../csvValidator';
import { LEXICON_MAX_ENTRIES } from '../limits';

const emptyDraft = {
  name: '',
  entries: [] as readonly Lexicon['entries'][0][],
};

export const lexiconsReducer = {
  [namespace]: createReducer(defaultState, (builder) => {
    builder
      .addCase(FETCH_LEXICONS, (state) => ({ ...state, isFetching: true }))
      .addCase(FETCH_LEXICONS_SUCCESS, (state, { payload }) => ({
        ...state,
        isFetching: false,
        isNotConfigured: false,
        lexicons: keyBy(payload.lexicons, 'id'),
        // Whether a page follows this one, as the query measured it. Not a
        // total — the registry cannot report one, the whole of VE-29495.
        hasMore: payload.hasMore,
        // Counted separately: a fact about records this page does not contain,
        // so it cannot be counted from `lexicons`. Capped — see the flag.
        deletedCount: payload.deletedCount,
        deletedCountExact: payload.deletedCountExact,
      }))
      /**
       * Taking the other actor's version, after a BR-6 conflict.
       *
       * Persisted entries are replaced by the fresh ones and the not-yet-saved
       * (`draft:`) entries are KEPT — the user's unsaved work is not what the
       * conflict was about. The baseline moves to the fresh revision so the
       * next save is guarded against what they just accepted, and the banner
       * clears.
       *
       * This used to hang off `FETCH_LEXICONS_SUCCESS`, back when the list
       * carried entries. It carries summaries now, so the reload is its own
       * read and its own action.
       */
      .addCase(RELOAD_EDITING_LEXICON_SUCCESS, (state, { payload }) => {
        const fresh = payload.lexicon;
        if (!state.editing || state.editing.lexiconId !== fresh.id) {
          return state;
        }
        const { conflict: _cleared, ...rest } = state.editing;
        return {
          ...state,
          isFetching: false,
          editing: {
            ...rest,
            draft: {
              ...rest.draft,
              entries: [
                ...fresh.entries,
                ...rest.draft.entries.filter((e) => isDraftEntryId(e.entryId)),
              ],
            },
            baseRevision: fresh.revision,
            baseline: { name: fresh.name, entries: fresh.entries },
          },
        };
      })
      .addCase(FETCH_LEXICONS_FAILURE, (state) => ({
        ...state,
        isFetching: false,
      }))
      .addCase(LEXICONS_NOT_CONFIGURED, (state) => ({
        ...state,
        isFetching: false,
        isNotConfigured: true,
        // Deliberately clears the list: showing stale lexicons while the
        // feature reports itself not configured would be worse than showing none.
        lexicons: {},
        hasMore: false,
        deletedCount: 0,
        deletedCountExact: true,
      }))
      .addCase(SET_PAGE, (state, { payload }) => ({
        ...state,
        offset: payload.offset,
        limit: payload.limit,
      }))
      .addCase(SET_SHOW_DELETED_LEXICONS, (state, { payload }) => ({
        ...state,
        showDeleted: payload.show,
      }))
      /**
       * Optimistic, and the saga is what makes it safe: a failed write
       * re-fetches the page, so the tick flicks back rather than lying.
       *
       * Ignores an id the page does not hold — a toggle can only come from a
       * rendered row, but a reducer that invents a row from a stale action
       * would put a lexicon with no name in the table.
       */
      .addCase(SET_LEXICON_FLAGS, (state, { payload }) => {
        const current = state.lexicons[payload.lexiconId];
        if (!current) {
          return state;
        }
        return {
          ...state,
          lexicons: {
            ...state.lexicons,
            [payload.lexiconId]: { ...current, ...payload.flags },
          },
        };
      })
      .addCase(OPEN_LEXICON_DRAWER, (state, { payload }) => {
        const existing = payload.lexiconId
          ? state.lexicons[payload.lexiconId]
          : undefined;
        return {
          ...state,
          isDrawerOpen: true,
          // OPENS WITHOUT ENTRIES, and a saga fills them in. The list holds
          // summaries — that is the point of the split — so the entries have to
          // be read, and `onOpenLexiconDrawer` reads them and dispatches
          // RELOAD_EDITING_LEXICON_SUCCESS, which is the same case that serves
          // the conflict banner's Reload. `isFetching` keeps the empty state
          // from flashing in the gap.
          isFetching: !!payload.lexiconId,
          editing: {
            ...(payload.lexiconId ? { lexiconId: payload.lexiconId } : {}),
            draft: existing ? { name: existing.name, entries: [] } : emptyDraft,
            // Captured on open — this is the value BR-6 compares against when
            // the write happens. Re-reading it later would defeat the guard.
            ...(existing
              ? {
                  baseRevision: existing.revision,
                  baseline: { name: existing.name, entries: [] },
                }
              : {}),
            entryErrors: {},
          },
        };
      })
      .addCase(CLOSE_LEXICON_DRAWER, (state) => {
        const { editing: _editing, csvImport: _csvImport, ...rest } = state;
        return { ...rest, isDrawerOpen: false };
      })
      .addCase(SET_DRAFT_NAME, (state, { payload }) =>
        state.editing
          ? {
              ...state,
              editing: {
                ...state.editing,
                draft: { ...state.editing.draft, name: payload.name },
              },
            }
          : state
      )
      .addCase(SET_DRAFT_NAME_ERROR, (state, { payload }) => {
        if (!state.editing) {
          return state;
        }
        const { nameError: _dropped, ...editing } = state.editing;
        return {
          ...state,
          editing: {
            ...editing,
            ...(payload.message ? { nameError: payload.message } : {}),
          },
        };
      })
      .addCase(ADD_DRAFT_ENTRIES, (state, { payload }) => {
        if (!state.editing) {
          return state;
        }
        const current = state.editing.draft.entries;
        const seen = new Set(current.map(lexiconEntryDuplicateKey));
        // Next free draft index: highest `draft:N` currently in the draft + 1.
        // Uniqueness among CURRENT entries is all that is needed — a removed
        // draft entry is referenced by nothing once it is gone.
        let next =
          current
            .filter((e) => isDraftEntryId(e.entryId))
            .reduce(
              (max, e) =>
                Math.max(
                  max,
                  Number(e.entryId.slice(DRAFT_ENTRY_ID_PREFIX.length)) || 0
                ),
              -1
            ) + 1;
        const headroom = Math.max(0, LEXICON_MAX_ENTRIES - current.length);
        const added: LexiconEntry[] = [];
        for (const input of payload.entries) {
          if (added.length >= headroom) {
            break; // BR-16 — the UI gates this too; the cap here is the floor.
          }
          const key = lexiconEntryDuplicateKey(input);
          if (seen.has(key)) {
            continue; // BR-23 — duplicates within a lexicon are skipped.
          }
          seen.add(key);
          added.push({ entryId: `${DRAFT_ENTRY_ID_PREFIX}${next}`, ...input });
          next += 1;
        }
        if (added.length === 0) {
          return state;
        }
        return {
          ...state,
          editing: {
            ...state.editing,
            draft: {
              ...state.editing.draft,
              entries: [...current, ...added],
            },
          },
        };
      })
      .addCase(RESTORE_DRAFT_ENTRY, (state, { payload }) =>
        state.editing
          ? {
              ...state,
              editing: {
                ...state.editing,
                draft: {
                  ...state.editing.draft,
                  entries: state.editing.draft.entries.map((e) =>
                    e.entryId === payload.entryId
                      ? { ...e, isDeleted: false }
                      : e
                  ),
                },
              },
            }
          : state
      )
      .addCase(PURGE_DRAFT_ENTRY, (state, { payload }) =>
        state.editing
          ? {
              ...state,
              editing: {
                ...state.editing,
                draft: {
                  ...state.editing.draft,
                  // Only a tombstone can be purged — a live entry is deleted
                  // first, so one control can never skip the recoverable step.
                  entries: state.editing.draft.entries.filter(
                    (e) => !(e.entryId === payload.entryId && e.isDeleted)
                  ),
                },
              },
            }
          : state
      )
      .addCase(PURGE_DELETED_DRAFT_ENTRIES, (state) =>
        state.editing
          ? {
              ...state,
              editing: {
                ...state.editing,
                draft: {
                  ...state.editing.draft,
                  entries: state.editing.draft.entries.filter(
                    (e) => !e.isDeleted
                  ),
                },
              },
            }
          : state
      )
      .addCase(UPDATE_DRAFT_ENTRY, (state, { payload }) => {
        if (!state.editing) {
          return state;
        }
        const current = state.editing.draft.entries;
        const index = current.findIndex((e) => e.entryId === payload.entryId);
        if (index === -1) {
          return state;
        }
        // BR-23 against the OTHER entries only. Comparing against all of them
        // would make every edit that leaves the duplicate key alone — changing
        // a redaction code, relabelling Keyword to Phrase — collide with the
        // entry being edited, i.e. with itself.
        const key = lexiconEntryDuplicateKey(payload.entry);
        const collides = current.some(
          (e, i) => i !== index && lexiconEntryDuplicateKey(e) === key
        );
        if (collides) {
          return state;
        }
        const existing = current[index];
        if (!existing) {
          return state;
        }
        const next = [...current];
        // Id and position preserved — that is what makes this an edit rather
        // than a remove plus an append, which would move the row to the bottom.
        next[index] = { entryId: existing.entryId, ...payload.entry };
        return {
          ...state,
          editing: {
            ...state.editing,
            draft: { ...state.editing.draft, entries: next },
          },
        };
      })
      .addCase(REMOVE_DRAFT_ENTRY, (state, { payload }) =>
        state.editing
          ? {
              ...state,
              editing: {
                ...state.editing,
                draft: {
                  ...state.editing.draft,
                  // Tombstone what was saved before; splice what never was.
                  // See the action for why the two differ.
                  entries: isDraftEntryId(payload.entryId)
                    ? state.editing.draft.entries.filter(
                        (e) => e.entryId !== payload.entryId
                      )
                    : state.editing.draft.entries.map((e) =>
                        e.entryId === payload.entryId
                          ? { ...e, isDeleted: true }
                          : e
                      ),
                },
              },
            }
          : state
      )
      .addCase(SET_LEXICON_CONFLICT, (state, { payload }) =>
        state.editing
          ? {
              ...state,
              editing: {
                ...state.editing,
                conflict: {
                  modifiedBy: payload.modifiedBy,
                  modifiedDateTime: payload.modifiedDateTime,
                },
              },
            }
          : state
      )
      .addCase(CLEAR_LEXICON_CONFLICT, (state) => {
        if (!state.editing) {
          return state;
        }
        const { conflict: _dropped, ...editing } = state.editing;
        return { ...state, editing };
      })
      /**
       * The PURGE, and only the purge.
       *
       * Delete no longer removes anything from this map — the record is still
       * there with `isDeleted` raised, which is what moves the row from one
       * list to the other. This case is what a permanent delete does, and it
       * really does drop the row.
       */
      .addCase(LEXICON_DELETED, (state, { payload }) => {
        const { [payload.lexiconId]: _removed, ...lexicons } = state.lexicons;
        return { ...state, lexicons };
      })
      .addCase(START_CSV_IMPORT, (state, { payload }) => ({
        ...state,
        csvImport: {
          fileName: payload.fileName,
          rowCount: payload.rowCount,
        },
      }))
      .addCase(SET_CSV_IMPORT_RESULT, (state, { payload }) =>
        state.csvImport
          ? {
              ...state,
              csvImport: { ...state.csvImport, result: payload.result },
            }
          : state
      )
      .addCase(CANCEL_CSV_IMPORT, (state) => {
        const { csvImport: _dropped, ...rest } = state;
        return rest;
      })

      // --- Mutation lifecycle (NFR-S4's UX half) --------------------------
      //
      // Five mutations share one in-flight flag and one outcome notice: the
      // page shows a single "did my change happen?" answer at a time, and the
      // three failure kinds are deliberately distinct — see
      // `LexiconMutationNotice` in models.ts.
      .addMatcher(
        (action) =>
          SAVE_LEXICON_DRAFT.match(action) ||
          CREATE_LEXICON.match(action) ||
          RENAME_LEXICON.match(action) ||
          ADD_LEXICON_ENTRIES.match(action) ||
          REMOVE_LEXICON_ENTRIES.match(action) ||
          DELETE_LEXICON.match(action),
        (state) => {
          const { mutationNotice: _dropped, ...rest } = state;
          return { ...rest, isMutating: true };
        }
      )
      .addMatcher(LEXICON_MUTATION_SUCCESS.match, (state, action) => {
        const written = action.payload.lexicon;
        // The dialog commits its draft in one Save and closes on success (the
        // saga dispatches CLOSE_LEXICON_DRAWER), so nothing here touches the
        // draft. The list refreshes via the FETCH_LEXICONS the saga dispatches
        // after every mutation; the written lexicon is stored eagerly so the
        // table is right before that fetch lands.
        const { mutationNotice: _dropped, ...rest } = state;
        return {
          ...rest,
          isMutating: false,
          // Stored as a SUMMARY — the list is summaries, and the entries the
          // write returned belong to the draft, not to this table.
          ...(written
            ? {
                lexicons: {
                  ...state.lexicons,
                  [written.id]: {
                    id: written.id,
                    name: written.name,
                    revision: written.revision,
                    entriesId: written.entriesId,
                    entryCount: liveEntries(written.entries).length,
                    deletedEntryCount: deletedEntries(written.entries).length,
                    createdDateTime: written.createdDateTime,
                    modifiedDateTime: written.modifiedDateTime,
                    createdBy: written.createdBy,
                    modifiedBy: written.modifiedBy,
                    // Required on the record, so it is copied rather than
                    // conditionally spread like the three below it.
                    isDeleted: written.isDeleted,
                    // The library flags ride along, or the row would show a
                    // disabled lexicon as enabled for the moment between a save
                    // and the fetch that follows it. Spread rather than
                    // defaulted: absent and false are different, and only the
                    // absent ones tell you which records predate the field.
                    ...(written.displayOrder !== undefined
                      ? { displayOrder: written.displayOrder }
                      : {}),
                    ...(written.enabled !== undefined
                      ? { enabled: written.enabled }
                      : {}),
                    ...(written.autoApply !== undefined
                      ? { autoApply: written.autoApply }
                      : {}),
                  },
                },
              }
            : {}),
        };
      })
      .addMatcher(LEXICON_MUTATION_BLOCKED_BY_AUDIT.match, (state, action) => ({
        ...state,
        isMutating: false,
        mutationNotice: { kind: 'blocked', message: action.payload.message },
      }))
      .addMatcher(LEXICON_MUTATION_AUDIT_INCOMPLETE.match, (state, action) => ({
        ...state,
        isMutating: false,
        mutationNotice: {
          kind: 'audit-incomplete',
          message: action.payload.message,
        },
      }))
      .addMatcher(LEXICON_MUTATION_FAILURE.match, (state, action) => ({
        ...state,
        isMutating: false,
        mutationNotice: { kind: 'failed', message: action.payload.message },
      }))
      // A conflict ends the in-flight state too — its surface is the drawer's
      // conflict banner (SET_LEXICON_CONFLICT above), not the notice.
      .addMatcher(SET_LEXICON_CONFLICT.match, (state) => ({
        ...state,
        isMutating: false,
      }))
      // So does a name-taken rejection (BR-2) — the saga bails before writing,
      // and its surface is the inline field error, not the notice.
      .addMatcher(SET_DRAFT_NAME_ERROR.match, (state) => ({
        ...state,
        isMutating: false,
      }))
      // Last word on the in-flight flag, dispatched from each mutation saga's
      // `finally`. Deliberately touches ONLY `isMutating`: it runs after an
      // outcome action, so clearing `mutationNotice` here would swallow the
      // very message that explains the failure.
      .addMatcher(LEXICON_MUTATION_SETTLED.match, (state) => ({
        ...state,
        isMutating: false,
      }))
      .addMatcher(CLEAR_MUTATION_NOTICE.match, (state) => {
        const { mutationNotice: _dropped, ...rest } = state;
        return rest;
      });
  }),
};

export type { LexiconsState };
